import {useEffect, useState} from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Box,
} from '@mui/material';
import {MoneyField} from '@/components/common/MoneyField';
import {useAdjustmentStore} from '@/store/audit/adjustmentStore';

export interface FixedAssetsRow {
    item: string;
    cost: number;
    tenPool: number;
    twentyPool: number;
    thirtyPool: number;
    hardAndSoftware: number;
}

interface FixedAssetsTableProps {
    parentCurrency: string | null;
    storeRow?: FixedAssetsRow[];
}

export function FixedAssetsTable({
                                     parentCurrency,
                                     storeRow: propStoreRow,
                                 }: FixedAssetsTableProps) {
    const queryTotalAdditionPPE = useAdjustmentStore((state) => state.queryTotalAdditionPPE);
    const [rows, setRows] = useState<FixedAssetsRow[]>(propStoreRow || []);

    useEffect(() => {
        if (propStoreRow) {
            setRows(propStoreRow);
            return;
        }

        const data = queryTotalAdditionPPE();

        // Convert the data to match the FixedAssetsRow interface
        const transformedRows: FixedAssetsRow[] = data.map((item) => ({
            item: item.item,
            cost: item.cost,
            tenPool: 0, // TODO: Calculate based on business logic
            twentyPool: 0, // TODO: Calculate based on business logic
            thirtyPool: 0, // TODO: Calculate based on business logic
            hardAndSoftware: 0, // TODO: Calculate based on business logic
        }));

        setRows(transformedRows);
    }, [queryTotalAdditionPPE, propStoreRow]);

    if (!rows || rows.length === 0) {
        return null;
    }

    // Calculate totals for each column
    const totals = rows.reduce(
        (acc, row) => ({
            cost: acc.cost + row.cost,
            tenPool: acc.tenPool + row.tenPool,
            twentyPool: acc.twentyPool + row.twentyPool,
            thirtyPool: acc.thirtyPool + row.thirtyPool,
            hardAndSoftware: acc.hardAndSoftware + row.hardAndSoftware,
        }),
        {
            cost: 0,
            tenPool: 0,
            twentyPool: 0,
            thirtyPool: 0,
            hardAndSoftware: 0,
        }
    );

    // Pre-scan: determine column visibility (omit if all values are zero)
    const showCost = rows.some((r) => r.cost !== 0);
    const showTenPool = rows.some((r) => r.tenPool !== 0);
    const showTwentyPool = rows.some((r) => r.twentyPool !== 0);
    const showThirtyPool = rows.some((r) => r.thirtyPool !== 0);
    const showHardAndSoftware = rows.some((r) => r.hardAndSoftware !== 0);

    // Count visible columns for colSpan
    const visibleDataCols = [showCost, showTenPool, showTwentyPool, showThirtyPool, showHardAndSoftware].filter(Boolean).length;

    return (
        <Box>
            <Table size="small">
                <TableHead>
                    <TableCell colSpan={1 + visibleDataCols}>
                        <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                            Addition to fixed assets
                        </Typography>
                    </TableCell>
                </TableHead>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{fontWeight: 'bold'}}></TableCell>
                        {showCost && (
                            <TableCell sx={{fontWeight: 'bold'}} align="center">
                                At Cost
                            </TableCell>
                        )}
                        {showTenPool && (
                            <TableCell sx={{fontWeight: 'bold'}} align="center">
                                10% pool
                            </TableCell>
                        )}
                        {showTwentyPool && (
                            <TableCell sx={{fontWeight: 'bold'}} align="center">
                                20% pool
                            </TableCell>
                        )}
                        {showThirtyPool && (
                            <TableCell sx={{fontWeight: 'bold'}} align="center">
                                30% pool
                            </TableCell>
                        )}
                        {showHardAndSoftware && (
                            <TableCell sx={{fontWeight: 'bold'}} align="center">
                                Computer hardware and software
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell>
                                <Typography variant="body2" sx={{fontWeight: 'bold'}}>
                                    {row.item}
                                </Typography>
                            </TableCell>
                            {showCost && (
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.cost}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                            )}
                            {showTenPool && (
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.tenPool}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                            )}
                            {showTwentyPool && (
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.twentyPool}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                            )}
                            {showThirtyPool && (
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.thirtyPool}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                            )}
                            {showHardAndSoftware && (
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.hardAndSoftware}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow sx={{backgroundColor: '#f5f5f5'}}>
                        <TableCell sx={{fontWeight: 'bold'}}>Total</TableCell>
                        {showCost && (
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.cost}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                        )}
                        {showTenPool && (
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.tenPool}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                        )}
                        {showTwentyPool && (
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.twentyPool}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                        )}
                        {showThirtyPool && (
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.thirtyPool}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                        )}
                        {showHardAndSoftware && (
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.hardAndSoftware}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                        )}
                    </TableRow>
                    <TableRow>
                        <TableCell colSpan={1 + visibleDataCols} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                    </TableRow>
                    <TableRow>
                        <TableCell colSpan={1 + visibleDataCols} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    );
}
